import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import tailwindClassnames from "../common/TailwindClassnames";
import { PredictionRunItem } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";

type Props = {
  predictionRuns: PredictionRunItem[];
  loading: boolean;
  showDeployment?: boolean;
  showFileCount?: boolean;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  contextLabel?: string;
  emptyMessage?: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (column: string) => void;
  onRetryFailedRun?: (run: PredictionRunItem) => void;
  retryingRunIds?: Set<number>;
};

// Badge: pill background + foreground. Uses /10 and /20 opacity modifiers
// (Tailwind v3 derives RGB channels from hex values so modifiers work on all
// custom tokens without any extra configuration).
const STATUS_BADGE: Record<string, string> = {
  completed:
    "bg-primary-green/10 text-primary-green dark:bg-primary-green/20 dark:text-primary-green-light",
  in_progress:
    "bg-primary-blue/10 text-primary-blue dark:bg-primary-blue/20 dark:text-primary-blue-light",
  queued:
    "bg-primary-purple/10 text-primary-purple-dark dark:bg-primary-purple/20 dark:text-primary-purple-light",
  // AKL-766: a "Run full pipeline" classification run before its detection
  // run has completed — visually paired with "queued" (same purple family)
  // since it is likewise waiting to start, just one step further back.
  pending:
    "bg-primary-purple/10 text-primary-purple-dark dark:bg-primary-purple/20 dark:text-primary-purple-light",
  failed: "bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  cancelled:
    "bg-storm-blue-light/10 text-off-black-light dark:bg-storm-blue-light/20 dark:text-greyed-text",
};

// Left-border accent on the expanded panel — one thick stroke per status.
const STATUS_BORDER: Record<string, string> = {
  completed: "border-l-primary-green",
  in_progress: "border-l-primary-blue",
  queued: "border-l-primary-purple",
  pending: "border-l-primary-purple",
  failed: "border-l-red-500",
  cancelled: "border-l-storm-blue-light",
};

const STATUS_LABELS: Record<string, string> = {
  completed: "Completed",
  in_progress: "In Progress",
  queued: "Queued",
  pending: "Pending",
  failed: "Failed",
  cancelled: "Cancelled",
};

const BASE_COL_COUNT = 6;

// ─── Inline SVG icons ────────────────────────────────────────────────────────
// All use stroke="currentColor" so they inherit whatever text-* class wraps
// them — no hardcoded colors anywhere.

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`transition-transform duration-300 ${
      expanded ? "rotate-180" : ""
    }`}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const DetectionIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
  </svg>
);

const ClassificationIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CopyIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const RetryIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const SortAscIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 15 12 9 18 15" />
  </svg>
);

const SortDescIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SortNeutralIcon = () => (
  <svg
    width="10"
    height="14"
    viewBox="0 0 16 22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="opacity-40"
  >
    <polyline points="2 8 8 2 14 8" />
    <polyline points="2 14 8 20 14 14" />
  </svg>
);

type SortableHeaderProps = {
  label: string;
  column: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (column: string) => void;
};

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  column,
  sortBy,
  sortDirection,
  onSort,
}) => {
  const isActive = sortBy === column;
  if (!onSort) return <>{label}</>;
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      onKeyDown={(e) => {
        if (e.key === " ") {
          e.preventDefault();
          onSort(column);
        }
      }}
      className={[
        "inline-flex items-center gap-1 cursor-pointer select-none outline-none bg-transparent border-0 p-0",
        "hover:text-primary-off-black dark:hover:text-primary-off-white",
        isActive ? "text-primary-off-black dark:text-primary-off-white" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
      {isActive ? (
        sortDirection === "asc" ? (
          <SortAscIcon />
        ) : (
          <SortDescIcon />
        )
      ) : (
        <SortNeutralIcon />
      )}
    </button>
  );
};

// ─── JSON syntax highlighter ─────────────────────────────────────────────────
// Every color class maps to an existing theme token:
//   keys        → sky-blue-dark  / sky-blue
//   strings     → primary-green  / primary-green-light
//   numbers     → primary-purple-dark / primary-purple-light
//   bool / null → primary-blue   / primary-blue-light
//   punctuation → off-black-light / greyed-text

type TokenType =
  | "key"
  | "string"
  | "number"
  | "boolean"
  | "null"
  | "punctuation"
  | "whitespace";

interface JsonToken {
  type: TokenType;
  value: string;
}

const TOKEN_CLASSES: Record<TokenType, string> = {
  key: "text-primary-blue dark:text-sky-blue",
  string: "text-primary-green dark:text-primary-green-light",
  number: "text-primary-purple-dark dark:text-primary-purple-light",
  boolean: "text-primary-blue-dark dark:text-primary-blue-light",
  null: "text-primary-blue-dark dark:text-primary-blue-light",
  punctuation: "text-off-black-light dark:text-greyed-text",
  whitespace: "",
};

function tokenizeJson(str: string): JsonToken[] {
  const tokens: JsonToken[] = [];
  let i = 0;
  while (i < str.length) {
    if (/\s/.test(str[i])) {
      let j = i;
      while (j < str.length && /\s/.test(str[j])) j++;
      tokens.push({ type: "whitespace", value: str.slice(i, j) });
      i = j;
      continue;
    }
    if (str[i] === '"') {
      let j = i + 1;
      while (j < str.length) {
        if (str[j] === "\\") {
          j += 2;
          continue;
        }
        if (str[j] === '"') {
          j++;
          break;
        }
        j++;
      }
      const raw = str.slice(i, j);
      let k = j;
      while (k < str.length && /\s/.test(str[k])) k++;
      tokens.push({ type: str[k] === ":" ? "key" : "string", value: raw });
      i = j;
      continue;
    }
    const numMatch = str
      .slice(i)
      .match(/^-?[0-9]+(\.[0-9]+)?([eE][+-]?[0-9]+)?/);
    if (numMatch) {
      tokens.push({ type: "number", value: numMatch[0] });
      i += numMatch[0].length;
      continue;
    }
    const kwMatch = str.slice(i).match(/^(true|false|null)/);
    if (kwMatch) {
      tokens.push({
        type: kwMatch[0] === "null" ? "null" : "boolean",
        value: kwMatch[0],
      });
      i += kwMatch[0].length;
      continue;
    }
    tokens.push({ type: "punctuation", value: str[i] });
    i++;
  }
  return tokens;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const PanelLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text mb-2">
    {children}
  </p>
);

const RunParametersJson: React.FC<{ json: unknown }> = React.memo(
  ({ json }) => {
    const tokens = useMemo(
      () => tokenizeJson(JSON.stringify(json, null, 2)),
      [json],
    );
    return (
      <pre className="overflow-x-auto text-sm font-mono leading-relaxed">
        {tokens.map((token, idx) => (
          <span key={idx} className={TOKEN_CLASSES[token.type]}>
            {token.value}
          </span>
        ))}
      </pre>
    );
  },
);

// ─── AKL-766 display helpers ─────────────────────────────────────────────────

/**
 * Client-derived run duration — there is no duration column on the backend,
 * so this is computed from the timestamps already on the row rather than
 * sorted/fetched from the server.
 */
function formatDuration(
  run: PredictionRunItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (run.status === "completed" && run.created_at && run.updated_at) {
    const minutes = Math.max(
      0,
      Math.round(
        (new Date(run.updated_at).getTime() -
          new Date(run.created_at).getTime()) /
          60000,
      ),
    );
    // Units go through t() — they are not universal abbreviations ("min" in
    // Spanish, "Min."/"Std." in German).
    return minutes < 60
      ? t("{{mins}}m", { mins: minutes })
      : t("{{hours}}h {{mins}}m", {
          hours: Math.floor(minutes / 60),
          mins: minutes % 60,
        });
  }
  if (run.status === "in_progress") return t("Running…");
  if (run.status === "queued" || run.status === "pending") return t("Waiting…");
  return "—";
}

/** run_parameters_json.confidence_threshold is stored as a 0-1 fraction. */
function formatConfThresh(json: Record<string, any> | undefined): string {
  const raw = json?.confidence_threshold;
  if (raw === undefined || raw === null || isNaN(Number(raw))) return "—";
  return `${Math.round(Number(raw) * 100)}%`;
}

/**
 * annotation_format is an internal ClearML task parameter with no bearing on
 * what a user needs from the raw JSON reveal — dropped for display only, the
 * stored run_parameters_json itself is untouched.
 */
function omitAnnotationFormat(json: Record<string, any>): Record<string, any> {
  const { annotation_format, ...rest } = json;
  return rest;
}

// ─── Main component ───────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const PredictionRunHistory: React.FC<Props> = ({
  predictionRuns,
  loading,
  showDeployment = false,
  showFileCount = false,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  contextLabel,
  emptyMessage,
  sortBy,
  sortDirection,
  onSort,
  onRetryFailedRun,
  retryingRunIds,
}) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [rawJsonId, setRawJsonId] = useState<number | null>(null);
  // AKL-766: client-side only (no backend search param) — filters the runs
  // already loaded for the current page. Real deployments/projects are
  // expected to accumulate a fairly short run history, so this covers the
  // common case without a round trip; it will not find a run sitting on a
  // different page of a very long history.
  const [search, setSearch] = useState("");
  const colCount =
    BASE_COL_COUNT + (showDeployment ? 1 : 0) + (showFileCount ? 1 : 0) + 1;

  const hasPagination =
    onPageChange !== undefined &&
    page !== undefined &&
    pageSize !== undefined &&
    totalCount !== undefined;
  const totalPages = hasPagination ? Math.ceil(totalCount! / pageSize!) : 1;

  const q = search.trim().toLowerCase();
  const visibleRuns = q
    ? predictionRuns.filter((run) =>
        [
          run.model_name,
          run.deployment_name,
          run.initiated_by_user_name,
          run.clearml_task_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q),
      )
    : predictionRuns;

  const toggle = (id: number) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const handleCopy = (taskId: string, runId: number) => {
    navigator.clipboard
      .writeText(taskId)
      .then(() => {
        setCopiedId(runId);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => {
        toast.error(t("Failed to copy to clipboard."));
      });
  };

  return (
    <div className="mt-4 w-full">
      {contextLabel && (
        <p className="mb-2 text-xs text-greyed-text dark:text-storm-blue-light">
          {contextLabel}
        </p>
      )}
      {/* Wider than the shared tableFilterInput's w-44 — this placeholder names
          four searchable fields and was truncating. `w-full` is appended so the
          wrapper's max-width wins, lining the box up with the max-w-md selects
          in the stage cards above. */}
      <div className="mb-3 max-w-md">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("Search model, deployment, person or task ID…")}
          aria-label={t("Search run history")}
          className={`${tailwindClassnames.tableFilterInput} w-full`}
        />
      </div>
      <div className="w-full overflow-x-auto">
        <table className="min-w-full table-fixed rounded shadow text-sm bg-off-white-dark dark:bg-storm-blue">
          {/* ── Header ── */}
          <thead>
            <tr className="border-b border-primary-off-white dark:border-storm-blue-light">
              <th className="w-8 px-3 py-2.5" />
              <th className="w-36 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                <SortableHeader
                  label={t("Status")}
                  column="status"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              <th className="w-40 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                <SortableHeader
                  label={t("Type")}
                  column="model_type"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              {showDeployment && (
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                  <SortableHeader
                    label={t("Deployment")}
                    column="deployment_name"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                </th>
              )}
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                <SortableHeader
                  label={t("Model")}
                  column="model_name"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              {showFileCount && (
                <th className="w-36 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                  <SortableHeader
                    label={t("Number of Files")}
                    column="file_count"
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                </th>
              )}
              <th className="w-52 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                <SortableHeader
                  label={t("Initiated By")}
                  column="initiated_by_user_name"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              <th className="w-60 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                <SortableHeader
                  label={t("Started")}
                  column="created_at"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              <th className="w-24 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-off-black-light dark:text-greyed-text">
                {t("Duration")}
              </th>
            </tr>
          </thead>

          {/* ── Body ── */}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} className="py-8 text-center">
                  <BeatLoader
                    color={tailwindClassnames.skyBlueDark}
                    loading
                    size={10}
                    margin={3}
                  />
                </td>
              </tr>
            ) : visibleRuns.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="py-8 text-center text-greyed-text dark:text-storm-blue-light"
                >
                  {q
                    ? t("No runs match your search.")
                    : (emptyMessage ?? t("No run history found."))}
                </td>
              </tr>
            ) : (
              visibleRuns.map((run) => {
                const isExpanded = expandedId === run.id;
                const isCopied = copiedId === run.id;
                const showRaw = rawJsonId === run.id;
                const badgeClass =
                  STATUS_BADGE[run.status] ?? STATUS_BADGE.cancelled;
                const borderClass =
                  STATUS_BORDER[run.status] ?? STATUS_BORDER.cancelled;
                const durationFmt = formatDuration(run, t);
                const confThreshFmt = formatConfThresh(run.run_parameters_json);

                return (
                  <React.Fragment key={run.id}>
                    {/* ── Summary row ── */}
                    <tr
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-controls={`run-detail-${run.id}`}
                      onClick={() => toggle(run.id)}
                      onKeyDown={(e) => {
                        // Only keys landing on the row itself toggle it. The
                        // nested Technical details / Retry / Copy buttons stop
                        // click propagation, but Enter and Space on them still
                        // bubble here as keydown and would collapse the row
                        // out from under the control just activated.
                        if (e.target !== e.currentTarget) return;
                        if (e.key === "Enter" || e.key === " ") {
                          // Space would otherwise scroll the page.
                          e.preventDefault();
                          toggle(run.id);
                        }
                      }}
                      className={[
                        "border-t border-primary-off-white dark:border-storm-blue-light",
                        "cursor-pointer select-none outline-none",
                        "transition-colors duration-150",
                        "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-blue-dark",
                        isExpanded
                          ? "bg-primary-off-white dark:bg-storm-blue-light"
                          : "bg-off-white-dark dark:bg-storm-blue hover:bg-primary-off-white dark:hover:bg-storm-blue-light",
                      ].join(" ")}
                    >
                      {/* Expand chevron */}
                      <td className="w-8 px-3 py-3 text-greyed-text dark:text-storm-blue-light">
                        <ChevronIcon expanded={isExpanded} />
                      </td>

                      {/* Status badge */}
                      <td className="px-4 py-3 text-left">
                        <span
                          className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClass}`}
                        >
                          {t(STATUS_LABELS[run.status] ?? run.status)}
                        </span>
                      </td>

                      {/* Type + icon */}
                      <td className="px-4 py-3 text-left">
                        <span className="inline-flex items-center gap-1.5 text-off-black-light dark:text-off-white-dark">
                          {run.model_type === "detector" ? (
                            <DetectionIcon />
                          ) : run.model_type === "classifier" ? (
                            <ClassificationIcon />
                          ) : null}
                          {run.model_type === "detector"
                            ? t("Detection")
                            : run.model_type === "classifier"
                              ? t("Classification")
                              : (run.model_type ?? "—")}
                        </span>
                      </td>

                      {/* Deployment */}
                      {showDeployment && (
                        <td className="px-4 py-3 text-left max-w-0 text-off-black-light dark:text-off-white-dark">
                          <span
                            className="block truncate"
                            title={run.deployment_name ?? undefined}
                          >
                            {run.deployment_name ?? "—"}
                          </span>
                        </td>
                      )}

                      {/* Model */}
                      <td className="px-4 py-3 text-left max-w-0 font-medium text-primary-off-black dark:text-primary-off-white">
                        <span
                          className="block truncate"
                          title={run.model_name ?? undefined}
                        >
                          {run.model_name ?? "—"}
                        </span>
                      </td>

                      {/* Number of Files */}
                      {showFileCount && (
                        <td className="px-4 py-3 text-left text-off-black-light dark:text-off-white-dark">
                          {run.file_count ?? "—"}
                        </td>
                      )}

                      {/* Initiated By */}
                      <td className="px-4 py-3 text-left max-w-0 text-off-black-light dark:text-off-white-dark">
                        <span
                          className="block truncate"
                          title={run.initiated_by_user_name ?? undefined}
                        >
                          {run.initiated_by_user_name ?? "—"}
                        </span>
                      </td>

                      {/* Started */}
                      <td className="px-4 py-3 text-left text-off-black-light dark:text-off-white-dark">
                        {run.created_at
                          ? new Date(run.created_at).toLocaleString()
                          : "—"}
                      </td>

                      {/* Duration */}
                      <td className="px-4 py-3 text-left text-off-black-light dark:text-off-white-dark">
                        {durationFmt}
                      </td>
                    </tr>

                    {/* ── Expanded detail row ── */}
                    {/* Always rendered; height animated via grid-template-rows trick
                      so the transition runs on both open and close. */}
                    <tr id={`run-detail-${run.id}`}>
                      <td colSpan={colCount} className="p-0 border-0">
                        <div
                          className="grid"
                          style={{
                            gridTemplateRows: isExpanded ? "1fr" : "0fr",
                            transition: "grid-template-rows 250ms ease",
                          }}
                        >
                          {/* `inert` while collapsed: the detail row stays
                              mounted so the close animation can run, and
                              overflow-hidden only clips it visually — without
                              this its buttons keep their place in the tab
                              order and the accessibility tree. */}
                          <div
                            className="overflow-hidden"
                            ref={(el) => {
                              if (el) el.inert = !isExpanded;
                            }}
                          >
                            {/* Left accent border — color tied to run status */}
                            <div
                              className={[
                                "border-l-4",
                                borderClass,
                                "bg-primary-off-white dark:bg-storm-blue-dark",
                                "px-6 py-5",
                              ].join(" ")}
                            >
                              <div className="grid grid-cols-2 gap-4">
                                {/* ── Left panel: metadata ── */}
                                <div className="rounded border border-primary-off-white dark:border-storm-blue-light bg-off-white-dark dark:bg-storm-blue shadow-sm p-4 space-y-5 text-center">
                                  {/* Started */}
                                  <div>
                                    <PanelLabel>{t("Started")}</PanelLabel>
                                    <span className="text-sm text-off-black-light dark:text-off-white-dark">
                                      {run.created_at
                                        ? new Date(
                                            run.created_at,
                                          ).toLocaleString()
                                        : "—"}
                                    </span>
                                  </div>

                                  {/* Last Updated */}
                                  <div>
                                    <PanelLabel>{t("Last Updated")}</PanelLabel>
                                    <span className="inline-flex items-center gap-1.5 text-sm text-off-black-light dark:text-off-white-dark">
                                      <span className="text-greyed-text dark:text-storm-blue-light">
                                        <ClockIcon />
                                      </span>
                                      {run.updated_at
                                        ? new Date(
                                            run.updated_at,
                                          ).toLocaleString()
                                        : "—"}
                                    </span>
                                  </div>

                                  {/* Initiated By */}
                                  <div>
                                    <PanelLabel>{t("Initiated By")}</PanelLabel>
                                    <span className="text-sm text-off-black-light dark:text-off-white-dark">
                                      {run.initiated_by_user_name ?? "—"}
                                    </span>
                                  </div>

                                  {/* Pending — waiting on its detection run */}
                                  {run.status === "pending" && (
                                    <div className="bg-primary-purple/10 dark:bg-primary-purple/20 rounded-lg px-3 py-2.5">
                                      <p className="m-0 text-sm text-primary-purple-dark dark:text-primary-purple-light">
                                        {run.waiting_on_run_id
                                          ? t(
                                              "Waiting for detection run #{{id}} to finish. Classification will start automatically once it completes.",
                                              { id: run.waiting_on_run_id },
                                            )
                                          : t(
                                              "Waiting for its detection run to finish. Classification will start automatically once it completes.",
                                            )}
                                      </p>
                                    </div>
                                  )}

                                  {/* Retry — failed runs only */}
                                  {run.status === "failed" &&
                                    onRetryFailedRun && (
                                      <div>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onRetryFailedRun(run);
                                          }}
                                          disabled={
                                            !!retryingRunIds?.has(run.id)
                                          }
                                          aria-label={t("Retry failed run")}
                                          className={[
                                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium",
                                            "bg-red-500/10 text-red-700 hover:bg-red-500/20",
                                            "dark:bg-red-500/20 dark:text-red-400 dark:hover:bg-red-500/30",
                                            "transition-colors duration-150",
                                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500",
                                            "disabled:opacity-50 disabled:cursor-not-allowed",
                                          ].join(" ")}
                                        >
                                          {retryingRunIds?.has(run.id) ? (
                                            <BeatLoader
                                              color="#b91c1c"
                                              loading
                                              size={6}
                                              margin={2}
                                            />
                                          ) : (
                                            <RetryIcon />
                                          )}
                                          {t("Retry failed run")}
                                        </button>
                                      </div>
                                    )}
                                </div>

                                {/* ── Right panel: run parameters (plain), + raw JSON behind a toggle ── */}
                                <div className="rounded border border-primary-off-white dark:border-storm-blue-light bg-off-white-dark dark:bg-storm-blue shadow-sm p-4">
                                  <PanelLabel>{t("Run Parameters")}</PanelLabel>
                                  <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1.5 text-sm">
                                    <dt className="text-greyed-text dark:text-storm-blue-light">
                                      {t("Model")}
                                    </dt>
                                    <dd className="m-0 text-off-black-light dark:text-off-white-dark">
                                      {run.model_name ?? "—"}
                                    </dd>
                                    <dt className="text-greyed-text dark:text-storm-blue-light">
                                      {t("Confidence threshold")}
                                    </dt>
                                    <dd className="m-0 text-off-black-light dark:text-off-white-dark">
                                      {confThreshFmt}
                                    </dd>
                                  </dl>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRawJsonId(showRaw ? null : run.id);
                                    }}
                                    aria-expanded={showRaw}
                                    className="mt-3 text-sm text-primary-blue dark:text-sky-blue underline cursor-pointer bg-transparent border-0 p-0"
                                  >
                                    {showRaw
                                      ? t("Hide technical details")
                                      : t("Technical details (raw JSON)")}
                                  </button>
                                  {showRaw && (
                                    <>
                                      <div className="mt-2.5 flex items-center gap-2 flex-wrap text-sm">
                                        <span className="text-greyed-text dark:text-storm-blue-light whitespace-nowrap">
                                          {t("Task ID")}
                                        </span>
                                        <code className="font-mono text-xs text-primary-off-black dark:text-primary-off-white overflow-hidden text-ellipsis whitespace-nowrap">
                                          {run.clearml_task_id ?? "—"}
                                        </code>
                                        {run.clearml_task_id && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCopy(
                                                run.clearml_task_id!,
                                                run.id,
                                              );
                                            }}
                                            aria-label={t("Copy Task ID")}
                                            className={[
                                              "flex-shrink-0 rounded p-1",
                                              "transition-colors duration-150",
                                              isCopied
                                                ? "text-primary-green dark:text-primary-green-light"
                                                : "text-greyed-text dark:text-storm-blue-light hover:text-primary-blue dark:hover:text-sky-blue",
                                            ].join(" ")}
                                          >
                                            {isCopied ? (
                                              <CheckIcon />
                                            ) : (
                                              <CopyIcon />
                                            )}
                                          </button>
                                        )}
                                      </div>
                                      {run.run_parameters_json ? (
                                        <RunParametersJson
                                          json={omitAnnotationFormat(
                                            run.run_parameters_json,
                                          )}
                                        />
                                      ) : (
                                        <span className="text-sm text-greyed-text dark:text-storm-blue-light">
                                          —
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {hasPagination && totalCount! > 0 && (
        <Pagination
          showText={true}
          handlePageClick={(e: { selected: number }) =>
            onPageChange!(e.selected + 1)
          }
          pageCount={totalPages}
          resultStart={(page! - 1) * pageSize! + 1}
          resultEnd={Math.min(page! * pageSize!, totalCount!)}
          totalCount={totalCount!}
          take={pageSize!}
          loading={loading}
          initialPage={page! - 1}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={onPageSizeChange}
          mainclassname="flex flex-row items-center justify-between mt-3 text-sm text-off-black-light dark:text-off-white-dark transition duration-300"
        />
      )}
    </div>
  );
};

export default PredictionRunHistory;
